import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Layout, Button, Form, Input, Select, message } from 'antd';
import LayoutHeader from '../../../../components/LayoutHeader';
import { Content } from 'antd/lib/layout/layout';
import { Utils } from '../../../common/services/Utils';
import PromptTemplateStore from '../../stores/PromptTemplatesStore';
import './PromptTemplate.less';
import { validateJson, prepareBlocksForSubmission, validateOrchestratedBlocks } from '../../misc';
import JsonEditor from './JsonEditor';
import { PromptTemplateBlock, PromptTemplateBlockTypeEnum, PromptTemplateStructureEnum } from '../../types';
import { BlocksManager } from '.';
import OrchestrationValidationAlert from './OrchestrationValidationAlert';
import { RouterStore } from '../../../common/stores';
import { Prompt } from 'react-router';

type Props = {
    store: PromptTemplateStore | undefined;
    router?: RouterStore;
};

const CreatePromptTemplate: React.FC<Props> = ({ store }) => {
    const [form] = Form.useForm();

    const [editorValue, setEditorValue] = React.useState<string>('');
    const [jsonIsInvalid, setJsonIsInvalid] = React.useState(false);
    const [blocks, setBlocks] = React.useState<PromptTemplateBlock[]>([
        {
            title: 'Block 1',
            comment: '',
            body: '',
            blockType: PromptTemplateBlockTypeEnum.Plain
        }
    ]);

    const [collapsedKeys, setCollapsedKeys] = React.useState<string[]>(['block-0']);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const [templateStructure, setTemplateStructure] = React.useState<PromptTemplateStructureEnum>(
        PromptTemplateStructureEnum.Merged
    );
    const [orchestrationErrors, setOrchestrationErrors] = React.useState<string[]>([]);

    const markAsChanged = () => setHasUnsavedChanges(true);

    if (!store) {
        return null;
    }

    const handleValueChange = (value: string) => {
        setJsonIsInvalid(false);
        setEditorValue(value);
        markAsChanged();
    };

    const handleGoBack = () => {
        store.navigateToList();
    };

    const handleSubmit = async () => {
        try {
            // Clear previous orchestration errors
            setOrchestrationErrors([]);

            const jsonIsValid = validateJson(editorValue);

            if (!jsonIsValid) {
                setJsonIsInvalid(true);
                return;
            }

            const values = await form.validateFields();
            const currentStructure = values.promptTemplateStructure || PromptTemplateStructureEnum.Merged;

            // Validate orchestration rules for Orchestrated templates
            const orchestrationValidation = validateOrchestratedBlocks(blocks, currentStructure);

            if (!orchestrationValidation.isValid) {
                setOrchestrationErrors(orchestrationValidation.errors);
                message.error('Failed to save the template. Please, see errors for more information.');
                return;
            }

            const blocksForSubmission = prepareBlocksForSubmission(blocks);

            setHasUnsavedChanges(false);

            await store.createTemplate(
                values.title,
                values.description,
                editorValue,
                currentStructure,
                blocksForSubmission
            );
        } catch (error) {
            setHasUnsavedChanges(true);
            console.log('Form validation failed');
        }
    };

    return (
        <Form form={form} layout="vertical">
            <Layout className="prompt-template-layout">
                <Prompt when={hasUnsavedChanges} message="You have unsaved changes. Are you sure you want to leave?" />
                <LayoutHeader
                    subtitle={Utils.getSubtitle(store.currentProject)}
                    helpMessage=""
                    title="Create Prompt Template"
                    buttons={[
                        ...(hasUnsavedChanges
                            ? [
                                  <span key="unsaved-changes-message" className="unsaved-changes-message">
                                      Unsaved changes
                                  </span>
                              ]
                            : []),
                        <Button
                            key="prompt-templates-go-back"
                            data-id="button-go-to-list"
                            className="light"
                            size="large"
                            onClick={handleGoBack}
                        >
                            Go back
                        </Button>,
                        <Button
                            key="prompt-templates-submit"
                            data-id="button-submit"
                            type="primary"
                            size="large"
                            onClick={handleSubmit}
                        >
                            Create Template
                        </Button>
                    ]}
                />
                <Layout>
                    <Content className="prompt-template-content">
                        <div className="content-padding">
                            <Form.Item name="blocks" hidden>
                                <Input type="hidden" />
                            </Form.Item>

                            <Form.Item
                                name="title"
                                label="Title"
                                rules={[
                                    { required: true, message: 'Please enter a template title' },
                                    { whitespace: true, message: 'Template title cannot be empty' }
                                ]}
                            >
                                <Input placeholder="Enter template name" style={{ flex: 1 }} onChange={markAsChanged} />
                            </Form.Item>
                            <Form.Item name="description" label="Description">
                                <Input placeholder="Enter template description" onChange={markAsChanged} />
                            </Form.Item>

                            <Form.Item
                                name="promptTemplateStructure"
                                label="Template Structure"
                                tooltip="Merged = single merged prompt; no step-by-step execution implied.
                                Orchestrated = explicit flow with fan-out (concurrent Processing) and fan-in (Post-Processing)."
                                rules={[{ required: true, message: 'Please select a template structure' }]}
                                initialValue={PromptTemplateStructureEnum.Merged}
                            >
                                <Select
                                    placeholder="Select template structure"
                                    onChange={(value: PromptTemplateStructureEnum) => {
                                        setTemplateStructure(value);
                                        setOrchestrationErrors([]); // Clear errors when structure changes
                                        markAsChanged();
                                    }}
                                >
                                    <Select.Option value={PromptTemplateStructureEnum.Merged}>Merged</Select.Option>
                                    <Select.Option value={PromptTemplateStructureEnum.Orchestrated}>
                                        Orchestrated
                                    </Select.Option>
                                </Select>
                            </Form.Item>

                            {/* Orchestration Validation Errors */}
                            <OrchestrationValidationAlert errors={orchestrationErrors} />

                            <BlocksManager
                                blocks={blocks}
                                collapsedKeys={collapsedKeys}
                                onBlocksChange={newBlocks => {
                                    setBlocks(newBlocks);
                                    setOrchestrationErrors([]); // Clear errors when blocks change
                                    markAsChanged();
                                }}
                                onCollapsedKeysChange={setCollapsedKeys}
                                templateStructure={templateStructure}
                            />

                            <JsonEditor
                                value={editorValue}
                                onChange={handleValueChange}
                                jsonIsInvalid={jsonIsInvalid}
                            />
                        </div>
                    </Content>
                </Layout>
            </Layout>
        </Form>
    );
};

export default observer(CreatePromptTemplate);
